import React, { useMemo, useState } from 'react';
import type { SharedArticleItem, SharedArticlesStats } from '../types';
import '@/features/file-hub/file-storage.css';

interface Props {
  articles: SharedArticleItem[];
  stats: SharedArticlesStats;
  onViewArticle: (article: SharedArticleItem) => void;
  onCreateArticleClick: () => void;
  onOpenSourceBase: () => void;
  onBack: () => void;
  onClose: () => void;
  onClearDetail?: () => void;
  detailArticle?: SharedArticleItem | null;
  detailSlot?: React.ReactNode;
}

/** Shared articles registry — styles: file-storage.css */
export const SharedArticlesView: React.FC<Props> = ({
  articles,
  stats,
  onViewArticle,
  onCreateArticleClick,
  onOpenSourceBase,
  onBack,
  onClose,
  onClearDetail,
  detailArticle = null,
  detailSlot = null,
}) => {
  const [search, setSearch] = useState('');

  const s = stats || {
    total_cards: articles.length,
    stages_count: 5,
    in_progress_count: 3,
    coeditors_count: 2,
  };

  const filtered = useMemo(() => {
    return articles.filter((a) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        a.title.toLowerCase().includes(q) ||
        a.journal.toLowerCase().includes(q) ||
        a.partner_label.toLowerCase().includes(q) ||
        a.article_code.toLowerCase().includes(q)
      );
    });
  }, [articles, search]);

  const stagesCount = useMemo(
    () => new Set(articles.map((x) => x.stage_label || x.stage_code)).size,
    [articles],
  );

  return (
    <div className="file-storage-overlay open" role="dialog" aria-modal="true" aria-label="Совместные статьи">
      <header className="file-storage-top">
        <div>
          <button
            className="file-storage-btn ghost"
            type="button"
            onClick={() => {
              if (detailArticle) {
                onClearDetail?.();
                return;
              }
              onBack();
            }}
          >
            ← Назад
          </button>
        </div>
        <div>
          <h2>{detailArticle ? 'Карточка статьи' : 'Совместные статьи'}</h2>
          <small>Владелец системы · общий реестр с научным партнёром</small>
        </div>
        <div className="file-storage-top-right">
          {!detailArticle ? (
            <button className="file-storage-btn" type="button" onClick={onBack}>
              Центр
            </button>
          ) : null}
          <button className="file-storage-btn danger" type="button" onClick={onClose}>
            Закрыть ×
          </button>
        </div>
      </header>

      <main className="file-storage-main">
        {detailArticle && detailSlot ? (
          detailSlot
        ) : (
          <div className="file-storage-wrap">
            <div className="file-storage-breadcrumb">
              Файлы и хранилище / <b>Совместные статьи</b>
            </div>

            <div className="file-storage-article-head">
              <div>
                <h3 style={{ margin: 0 }}>Совместные статьи / заказы</h3>
                <p style={{ margin: '4px 0 0', color: '#8facbc', fontSize: 14 }}>
                  Общая рабочая карточка владельца и научного партнёра / коллеги. Изменения сохраняются в
                  одной записи.
                </p>
              </div>
              <button type="button" className="file-storage-btn primary" onClick={onCreateArticleClick}>
                + Новый заказ
              </button>
            </div>

            <div className="file-storage-panel" style={{ marginBottom: 12, borderColor: 'rgba(226,173,58,.38)' }}>
              <h4 style={{ margin: '0 0 6px' }}>Связанный реестр: «Заказы клиентов»</h4>
              <p>
                Карточки совместных статей связаны с реестром заказов клиентов. Откройте заказ, чтобы посмотреть его сведения и материалы.
              </p>
              <button
                type="button"
                className="file-storage-btn gold"
                style={{ display: 'inline-flex', marginTop: 10 }}
                onClick={onOpenSourceBase}
              >
                Открыть заказы клиентов
              </button>
            </div>

            <div className="file-storage-article-stats">
              <div className="file-storage-stat">
                <b>{s.total_cards || articles.length}</b>
                <span>все карточки</span>
              </div>
              <div className="file-storage-stat">
                <b>{s.stages_count || stagesCount}</b>
                <span>этапы</span>
              </div>
              <div className="file-storage-stat">
                <b>{s.in_progress_count}</b>
                <span>в процессе</span>
              </div>
              <div className="file-storage-stat">
                <b>{s.coeditors_count}</b>
                <span>совместных редактора</span>
              </div>
            </div>

            <div style={{ margin: '12px 0' }}>
              <input
                className="file-storage-search"
                placeholder="Поиск по названию, журналу, партнёру…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="file-storage-articles">
              {filtered.map((x) => (
                <article key={x.id} className="file-storage-article">
                  <div>
                    <h4>{x.title}</h4>
                    <small>
                      {x.journal} · обновлено {x.updated_date || '—'}
                    </small>
                  </div>
                  <div>
                    <span className="file-storage-stage">{x.stage_label || x.status}</span>
                  </div>
                  <div>
                    <b style={{ fontSize: 12 }}>{x.partner_label}</b>
                  </div>
                  <div className="file-storage-pay">
                    <small>Оплата</small>
                    <div>{x.payment_state}</div>
                  </div>
                  <button type="button" className="file-storage-btn" onClick={() => onViewArticle(x)}>
                    Открыть
                  </button>
                </article>
              ))}
              {!filtered.length ? (
                <div className="file-storage-lock-msg">
                  <h3>Нет карточек</h3>
                  <p>По текущему поиску совместных статей не найдено.</p>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
